import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CreditCard, AlertCircle, Upload, Link as LinkIcon, Video, X, CheckCircle, Sparkles, Loader2, Mic, MicOff } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CritiqueRequestPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [videoMethod, setVideoMethod] = useState('link'); // 'link', 'upload', 'shared'
  const [videoLinks, setVideoLinks] = useState(['']);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uploadedVideoUrls, setUploadedVideoUrls] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [sharedDirectly, setSharedDirectly] = useState(false);
  const [note, setNote] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [critiquePackage, setCritiquePackage] = useState('tuneup');
  const [sharing, setSharing] = useState('private');
  const [coachId, setCoachId] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [enhancingNote, setEnhancingNote] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);

  // Critique packages
  const critiquePackages = [
    { id: 'tuneup', name: 'Rodeo Tune-Up', credits: 15, description: 'Quick feedback on specific areas' },
    { id: 'shortgo', name: 'Short Go', credits: 40, description: 'Comprehensive video analysis' },
    { id: 'elevated', name: 'Elevated', credits: 100, description: 'Full coaching session with detailed breakdown' }
  ];

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setNote(prev => {
            const newNote = prev ? prev + ' ' + finalTranscript : finalTranscript;
            return newNote.slice(0, 500);
          });
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Don't stop on 'no-speech' or 'aborted' - these are normal
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;
        }
        shouldRestartRef.current = false;
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please allow microphone access in your browser settings.');
        }
      };

      recognition.onend = () => {
        // Auto-restart if we should still be listening
        if (shouldRestartRef.current) {
          try {
            recognition.start();
          } catch (err) {
            console.error('Failed to restart speech recognition:', err);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        shouldRestartRef.current = false;
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    fetchDisciplines();
    fetchCoaches();
    if (user?.disciplines?.length > 0) {
      setDiscipline(user.disciplines[0]);
    }
  }, [user]);

  const fetchDisciplines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/disciplines`);
      setDisciplines(response.data.disciplines || []);
    } catch (error) {
      setDisciplines(['Heading', 'Heeling', 'Breakaway', 'Ranch Roping']);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches`);
      const coachList = response.data.coaches || [];
      setCoaches(coachList);
      // Auto-select first coach if only one
      if (coachList.length === 1) {
        setCoachId(coachList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    }
  };

  const selectedPackage = critiquePackages.find(p => p.id === critiquePackage);
  const creditsNeeded = selectedPackage?.credits || 15;
  const hasEnoughCredits = (user?.credits || 0) >= creditsNeeded;

  const handleVideoFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/mov'];
    const validFiles = [];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setError(`${file.name}: Invalid format. Use MP4, MOV, AVI, or WebM`);
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        setError(`${file.name}: Must be less than 500MB`);
        return;
      }
      validFiles.push(file);
    }
    
    if (videoFiles.length + validFiles.length > 3) {
      setError('You can upload a maximum of 3 videos');
      return;
    }
    
    setError('');
    const newFiles = [...videoFiles, ...validFiles];
    setVideoFiles(newFiles);
    
    // Upload each new file
    setUploading(true);
    for (const file of validFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'critiques');
      
      try {
        const response = await axios.post(`${API_URL}/api/upload/video`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [file.name]: pct }));
          }
        });
        setUploadedVideoUrls(prev => [...prev, response.data.url]);
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
      } catch (err) {
        setError(err.response?.data?.detail || `Failed to upload ${file.name}`);
        setVideoFiles(prev => prev.filter(f => f.name !== file.name));
      }
    }
    setUploading(false);
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeVideoFile = (index) => {
    setVideoFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedVideoUrls(prev => prev.filter((_, i) => i !== index));
    const newProgress = { ...uploadProgress };
    delete newProgress[videoFiles[index]?.name];
    setUploadProgress(newProgress);
  };

  const toggleListening = () => {
    if (!recognitionRef.current) return;

    if (isListening) {
      shouldRestartRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setError('');
      shouldRestartRef.current = true;
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
        // If already started, stop and restart
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current.start();
            setIsListening(true);
          } catch (e) {
            console.error('Failed to restart:', e);
            shouldRestartRef.current = false;
            setIsListening(false);
          }
        }, 100);
      }
    }
  };

  const enhanceNoteWithAI = async () => {
    if (!note.trim() || note.length < 10) {
      setError('Please write at least a few words about what you want feedback on.');
      return;
    }

    setEnhancingNote(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/ai/enhance-note`, {
        note: note,
        discipline: discipline || 'roping'
      });
      setNote(response.data.enhanced_note);
    } catch (err) {
      console.error('Failed to enhance note:', err);
      setError('Failed to enhance note. Please try again.');
    } finally {
      setEnhancingNote(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Determine the video link based on method
    let finalVideoLink = null;
    let additionalVideos = [];
    if (videoMethod === 'link') {
      const validLinks = videoLinks.filter(l => l.trim());
      if (validLinks.length === 0) {
        setError('Please provide at least one video link.');
        return;
      }
      finalVideoLink = validLinks[0];
      additionalVideos = validLinks.slice(1);
    } else if (videoMethod === 'upload') {
      if (uploadedVideoUrls.length === 0) {
        setError('Please upload at least one video file.');
        return;
      }
      finalVideoLink = uploadedVideoUrls[0];
      additionalVideos = uploadedVideoUrls.slice(1);
    }
    // For 'shared' method, finalVideoLink stays null

    if (!discipline) {
      setError('Please select a discipline.');
      return;
    }

    if (!coachId) {
      setError('Please select a coach.');
      return;
    }

    if (!hasEnoughCredits) {
      setError(`Not enough Elevated Credits. You need ${creditsNeeded}.`);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/critiques`, {
        video_link: finalVideoLink,
        additional_videos: additionalVideos,
        shared_directly: videoMethod === 'shared',
        note,
        discipline,
        critique_package: critiquePackage,
        sharing,
        coach_id: coachId
      });
      
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit critique request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            New Critique Request
          </h1>
          <p className="text-muted-foreground mt-2">
            Submit up to 3 videos for coach review. Short clips (1-3 minutes each) work best.
          </p>
        </div>

        {/* Credits Display */}
        <Card className="mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-primary" />
              <div>
                <p className="font-semibold">Your Elevated Credits: {user?.credits || 0}</p>
                <p className="text-sm text-muted-foreground">
                  This request will use {creditsNeeded} Elevated Credit{creditsNeeded > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            {!hasEnoughCredits && (
              <Link to="/subscription">
                <Button variant="outline" size="sm" className="rounded-full">
                  Buy Elevated Credits
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl uppercase">Video Details</CardTitle>
            <CardDescription>
              Upload up to 3 videos or share via link (YouTube unlisted, Google Drive, Dropbox, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Video Method Selection */}
              <div className="space-y-4">
                <Label>How would you like to share your video?</Label>
                <RadioGroup value={videoMethod} onValueChange={(val) => {
                  setVideoMethod(val);
                  setError('');
                }} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${videoMethod === 'upload' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value="upload" id="method-upload" />
                    <Label htmlFor="method-upload" className="cursor-pointer flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload File
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${videoMethod === 'link' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value="link" id="method-link" />
                    <Label htmlFor="method-link" className="cursor-pointer flex items-center gap-2">
                      <LinkIcon className="w-4 h-4" />
                      Paste Link
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${videoMethod === 'shared' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value="shared" id="method-shared" />
                    <Label htmlFor="method-shared" className="cursor-pointer flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Already Shared
                    </Label>
                  </div>
                </RadioGroup>

                {/* Upload File Section */}
                {videoMethod === 'upload' && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">You can upload up to 3 videos for this critique.</p>
                    {videoFiles.map((file, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center gap-3">
                          <Video className="w-8 h-8 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{file.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {(file.size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                          </div>
                          {!uploading && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeVideoFile(idx)} className="h-8 w-8 p-0">
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                          <div className="mt-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Uploading...</span>
                              <span>{uploadProgress[file.name]}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[file.name]}%` }} />
                            </div>
                          </div>
                        )}
                        {uploadedVideoUrls[idx] && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-secondary">
                            <CheckCircle className="w-4 h-4" />
                            Upload complete
                          </div>
                        )}
                      </div>
                    ))}
                    {videoFiles.length < 3 && (
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                      >
                        <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="font-medium">{videoFiles.length === 0 ? 'Click to upload video' : 'Add another video'}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          MP4, MOV, AVI, WebM up to 500MB ({videoFiles.length}/3 videos)
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoFileSelect}
                      className="hidden"
                    />
                  </div>
                )}

                {/* Link Section */}
                {videoMethod === 'link' && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">You can add up to 3 video links for this critique.</p>
                    {videoLinks.map((link, idx) => (
                      <div key={idx} className="flex gap-2">
                        <div className="flex-1 space-y-1">
                          <Label htmlFor={`videoLink-${idx}`}>Video Link {idx + 1}</Label>
                          <Input
                            id={`videoLink-${idx}`}
                            type="url"
                            placeholder="https://youtube.com/watch?v=... or drive.google.com/..."
                            value={link}
                            onChange={(e) => {
                              const updated = [...videoLinks];
                              updated[idx] = e.target.value;
                              setVideoLinks(updated);
                            }}
                            className="h-12"
                            data-testid={`video-link-input-${idx}`}
                          />
                        </div>
                        {videoLinks.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" className="mt-7 h-12 w-10 p-0"
                            onClick={() => setVideoLinks(prev => prev.filter((_, i) => i !== idx))}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {videoLinks.length < 3 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => setVideoLinks(prev => [...prev, ''])}
                        data-testid="add-video-link-btn">
                        + Add Another Link ({videoLinks.length}/3)
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      YouTube, Google Drive, Dropbox, or any shareable link
                    </p>
                  </div>
                )}

                {/* Already Shared Section */}
                {videoMethod === 'shared' && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      You've indicated that you've already shared the video directly with the coach 
                      (via text, email, or another method). The coach will look for your video in their messages.
                    </p>
                  </div>
                )}
              </div>

              {/* Coach Selection */}
              {coaches.length > 0 && (
                <div className="space-y-2">
                  <Label>Select Coach</Label>
                  <Select value={coachId} onValueChange={setCoachId}>
                    <SelectTrigger className="h-12" data-testid="coach-select">
                      <SelectValue placeholder="Choose a coach for your critique" />
                    </SelectTrigger>
                    <SelectContent>
                      {coaches.map(coach => (
                        <SelectItem key={coach.id} value={coach.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{coach.name}</span>
                            {coach.region && (
                              <span className="text-xs text-muted-foreground">{coach.region}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {coaches.length === 1 && (
                    <p className="text-xs text-muted-foreground">
                      Your critique will be reviewed by {coaches[0].name}
                    </p>
                  )}
                </div>
              )}

              {/* Discipline */}
              <div className="space-y-2">
                <Label>Discipline</Label>
                <Select value={discipline} onValueChange={setDiscipline}>
                  <SelectTrigger className="h-12" data-testid="discipline-select">
                    <SelectValue placeholder="Select your discipline" />
                  </SelectTrigger>
                  <SelectContent>
                    {disciplines.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Note */}
              <div className="space-y-2">
                <Label htmlFor="note">Note to Coach</Label>
                <div className="relative">
                  <Textarea
                    id="note"
                    placeholder="What are you working on? Any specific areas you want feedback on? (e.g., 'Working on my heading position at the catch' or 'Trying to improve my swing timing')"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    rows={4}
                    className={isListening ? 'border-primary ring-2 ring-primary/20' : ''}
                    data-testid="note-textarea"
                  />
                  {isListening && (
                    <div className="absolute top-2 right-2 flex items-center gap-2 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      Listening...
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {speechSupported && (
                      <Button
                        type="button"
                        variant={isListening ? "default" : "outline"}
                        size="sm"
                        onClick={toggleListening}
                        className={`gap-2 ${isListening ? 'bg-red-500 hover:bg-red-600' : ''}`}
                        data-testid="voice-input-btn"
                      >
                        {isListening ? (
                          <>
                            <MicOff className="w-4 h-4" />
                            Stop
                          </>
                        ) : (
                          <>
                            <Mic className="w-4 h-4" />
                            Speak
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={enhanceNoteWithAI}
                      disabled={enhancingNote || !note.trim() || note.length < 10}
                      className="gap-2"
                      data-testid="enhance-note-btn"
                    >
                      {enhancingNote ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enhancing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          AI Enhance
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{note.length}/500</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {speechSupported 
                    ? 'Speak or type your thoughts, then click "AI Enhance" to improve spelling and clarity'
                    : 'Type your thoughts and click "AI Enhance" to improve spelling and clarity'
                  }
                </p>
              </div>

              {/* Critique Package */}
              <div className="space-y-3">
                <Label>Select Your Package</Label>
                <RadioGroup value={critiquePackage} onValueChange={setCritiquePackage} className="space-y-3">
                  {critiquePackages.map(pkg => (
                    <div 
                      key={pkg.id}
                      className={`flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${critiquePackage === pkg.id ? 'border-primary bg-primary/5' : ''}`}
                    >
                      <RadioGroupItem value={pkg.id} id={pkg.id} data-testid={`package-${pkg.id}`} />
                      <Label htmlFor={pkg.id} className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{pkg.name}</span>
                          <span className="text-primary font-bold">{pkg.credits} Elevated Credits</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Sharing */}
              <div className="space-y-3">
                <Label>Sharing Preference</Label>
                <RadioGroup value={sharing} onValueChange={setSharing} className="space-y-2">
                  <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="private" id="private" data-testid="sharing-private" />
                    <Label htmlFor="private" className="flex-1 cursor-pointer">
                      <span className="font-semibold">Private Only</span>
                      <p className="text-sm text-muted-foreground">Only you and the coach can see this</p>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="anonymized" id="anonymized" data-testid="sharing-anonymized" />
                    <Label htmlFor="anonymized" className="flex-1 cursor-pointer">
                      <span className="font-semibold">Share Anonymously</span>
                      <p className="text-sm text-muted-foreground">Help others learn from the coach's feedback</p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-muted-foreground">Package: </span>
                    <span className="font-semibold">{selectedPackage?.name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Elevated Credits: </span>
                    <span className="font-bold text-lg text-primary">{creditsNeeded}</span>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !hasEnoughCredits}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide"
                  data-testid="submit-critique-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Critique Request'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CritiqueRequestPage;
